import type { MeResponse, CreateSessionRequest, SessionResponse, ApiResponse, ApiAdapters, ActionResult, Delegation, ActionConfirmation } from './types';

export interface UsageResponse {
  seconds: number;
  estimated_usd: number | null;
  input_tokens?: number;
  output_tokens?: number;
  final?: boolean;
  costs?: CostSummary;
}

export interface CostPeriod {
  known_estimated_usd: number;
  partial: boolean;
  unknown_components: string[];
  reasons: string[];
}

export interface CostSummary {
  currency: string;
  timezone: string;
  rate_version: string;
  periods: { conversation: CostPeriod | null; week_to_date: CostPeriod; month_to_date: CostPeriod };
}

export interface DelegationsResponse {
  delegations: Delegation[];
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

export class VoiceApi {
  private csrf = '';

  constructor(private adapters: ApiAdapters, private baseUrl: string = '') {}

  setCsrf(token: string) {
    this.csrf = token;
  }

  async me(): Promise<MeResponse> {
    return this.json('GET', '/api/me') as Promise<MeResponse>;
  }

  async command(request: { text: string; conversation_id?: string; selected_delegation_id?: string; request_id: string; timezone?: string }): Promise<{
    conversation_id: string; request_id: string; text: string;
    confirmation?: ActionConfirmation; delegation?: Delegation;
  }> {
    return this.json('POST', '/api/commands', request) as ReturnType<VoiceApi['command']>;
  }

  async transcribe(audio: Blob, signal: AbortSignal, conversationId?: string): Promise<{ text: string }> {
    const response = await this.audioRequest('/api/audio/transcribe', audio, 'audio/wav', signal, conversationId);
    return response.json() as Promise<{ text: string }>;
  }

  async speech(text: string, signal: AbortSignal, conversationId?: string): Promise<Blob> {
    const response = await this.audioRequest('/api/audio/speech', JSON.stringify({ text, conversation_id: conversationId }), 'application/json', signal);
    if (!response.blob) throw new ApiError('Audio playback is unavailable.', 503);
    return response.blob();
  }

  private async audioRequest(path: string, body: BodyInit, contentType: string, signal: AbortSignal, conversationId?: string): Promise<ApiResponse> {
    const response = await this.adapters.fetch(this.baseUrl + path, {
      method: 'POST', credentials: 'same-origin', signal,
      headers: { 'Content-Type': contentType, 'X-CSRF-Token': this.csrf, ...(conversationId ? { 'X-CM-Conversation-ID': conversationId } : {}) }, body,
    });
    if (!response.ok) throw new ApiError('Audio request failed. Please try again or type your command.', response.status);
    return response;
  }

  async createSession(req: CreateSessionRequest): Promise<SessionResponse> {
    return this.json('POST', '/api/voice/sessions', req) as Promise<SessionResponse>;
  }

  async closeSession(sessionId: string): Promise<{ ok: boolean; finalized: boolean; usage_seconds?: number | null }> {
    return this.json('POST', `/api/voice/${encodeURIComponent(sessionId)}/close`) as Promise<{ ok: boolean; finalized: boolean; usage_seconds?: number | null }>;
  }

  async confirmAction(actionId: string, manifest_hash: string, epoch: number): Promise<{ action: ActionResult }> {
    return this.json('POST', `/api/actions/${encodeURIComponent(actionId)}/confirm`, { manifest_hash, epoch }) as Promise<{ action: ActionResult }>;
  }

  async cancelAction(actionId: string, manifest_hash: string, epoch: number): Promise<{ action: ActionResult }> {
    return this.json('POST', `/api/actions/${encodeURIComponent(actionId)}/cancel`, { manifest_hash, epoch }) as Promise<{ action: ActionResult }>;
  }

  async getDelegations(): Promise<DelegationsResponse> {
    return this.json('GET', '/api/delegations') as Promise<DelegationsResponse>;
  }

  async getPendingActions(): Promise<{ actions: ActionConfirmation[] }> {
    return this.json('GET', '/api/actions/pending') as ReturnType<VoiceApi['getPendingActions']>;
  }

  async getConversations(): Promise<{ conversations: Array<{ id: string; created_at: string; updated_at: string }> }> {
    return this.json('GET', '/api/conversations') as ReturnType<VoiceApi['getConversations']>;
  }

  async resumeConversation(id: string): Promise<{ conversation_id: string }> {
    return this.json('POST', `/api/conversations/${encodeURIComponent(id)}/resume`, {}) as ReturnType<VoiceApi['resumeConversation']>;
  }

  async createConversation(): Promise<{ conversation_id: string }> {
    return this.json('POST', '/api/conversations', {}) as Promise<{ conversation_id: string }>;
  }

  async getUsage(conversationId?: string, timezone?: string): Promise<UsageResponse> {
    const params = new URLSearchParams();
    if (conversationId) params.set('conversation_id', conversationId);
    if (timezone) params.set('timezone', timezone);
    return this.json('GET', '/api/usage' + (params.size ? '?' + params.toString() : '')) as Promise<UsageResponse>;
  }

  async logout(): Promise<{ ok: boolean }> {
    return this.json('POST', '/auth/logout') as Promise<{ ok: boolean }>;
  }

  eventsSocket(sessionId: string): WebSocket {
    return new this.adapters.WebSocket(this.wsUrl(`/api/voice/${encodeURIComponent(sessionId)}/events`));
  }

  private async json(method: 'GET' | 'POST', path: string, body?: unknown): Promise<unknown> {
    const headers: Record<string, string> = {};
    if (method !== 'GET') {
      headers['Content-Type'] = 'application/json';
      if (this.csrf) headers['X-CSRF-Token'] = this.csrf;
    }
    const res = await this.adapters.fetch(this.baseUrl + path, {
      method,
      headers,
      credentials: 'same-origin',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      let data: any = {};
      try {
        data = await res.json();
      } catch {
        // no-op
      }
      const message = data?.error?.message || `${method} ${path} failed (${res.status})`;
      throw new ApiError(message, res.status, typeof data?.error?.code === 'string' ? data.error.code : undefined);
    }
    return res.json();
  }

  private wsUrl(path: string): string {
    const base = this.baseUrl || this.adapters.location.href;
    const url = new URL(base);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}${path}`;
  }
}
